import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  getOddMovements,
  getTrackedMarkets,
  getOddsHistoryMatch,
  OddMovementsParams,
  searchOddsHistoryMatches,
} from "@/api/oddsHistory";
import { OddsHistoryQueryKeys } from "./oddsHistoryQueryKeys";

// Refetched on the collector's own cadence: polling faster than the job writes
// only produces identical responses.
export const useOddMovements = (params: OddMovementsParams) =>
  useQuery({
    queryKey: [OddsHistoryQueryKeys.Movements, params],
    placeholderData: keepPreviousData,
    refetchInterval: 180_000,
    queryFn: ({ signal }) => getOddMovements({ signal, params }),
  });

// `matchKey` is null until a row is selected; `enabled` keeps the drawer query
// from firing before there is anything to show.
export const useOddsHistoryMatch = (matchKey: string | null, lang: string) =>
  useQuery({
    queryKey: [OddsHistoryQueryKeys.MatchTimeline, matchKey, lang],
    enabled: matchKey !== null,
    queryFn: ({ signal }) =>
      getOddsHistoryMatch({
        signal,
        params: { matchKey: matchKey as string, lang },
      }),
  });

// Market groups the collector records, named by the server from the same
// mapping tables the board uses. Static for the session — hence no refetch.
export const useTrackedMarkets = (lang: string) =>
  useQuery({
    queryKey: [OddsHistoryQueryKeys.Markets, lang],
    staleTime: Infinity,
    queryFn: ({ signal }) => getTrackedMarkets({ signal, params: { lang } }),
  });

export const useOddsHistoryMatchSearch = (search: string) =>
  useQuery({
    queryKey: [OddsHistoryQueryKeys.MatchSearch, search],
    enabled: search.trim().length >= 2,
    queryFn: ({ signal }) =>
      searchOddsHistoryMatches({ signal, params: { search } }),
  });
